import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Paper, FormControl, InputLabel, Select, MenuItem,
  Button, CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, LinearProgress, Collapse, IconButton, Tabs, Tab
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';

const MODULE_LABELS = {
  academics: 'Academics',
  finance: 'Finance',
  staffmanagement: 'Staff Management',
  communication: 'Communication',
  subjectmanagement: 'Subject Management',
  classmanagement: 'Class Management',
  facilities: 'Facilities',
  support: 'Support',
  parentmodule: 'Parent Module',
  teachermodule: 'Teacher Module'
};

const MODULE_COLORS = {
  academics: '#1976d2',
  finance: '#388e3c',
  staffmanagement: '#f57c00',
  communication: '#7b1fa2',
  subjectmanagement: '#0097a7',
  classmanagement: '#c62828',
  facilities: '#5d4037',
  support: '#455a64',
  parentmodule: '#ad1457',
  teachermodule: '#1565c0'
};

function PercentBar({ value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(value, 100)}
        sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: color || '#1976d2' } }}
      />
      <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>{value}%</Typography>
    </Box>
  );
}

function SchoolRow({ row }) {
  const [open, setOpen] = useState(false);
  const total = row.totalActions || 0;

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
            <strong>{row.schoolName}</strong>
          </Box>
        </TableCell>
        {Object.keys(MODULE_LABELS).map(mod => {
          const cnt = row.modules?.[mod] || 0;
          const pct = total > 0 ? ((cnt / total) * 100).toFixed(1) : '0.0';
          return (
            <TableCell key={mod} align="center">
              {cnt > 0 ? <Chip label={`${pct}%`} size="small" sx={{ bgcolor: MODULE_COLORS[mod] + '22', color: MODULE_COLORS[mod], fontWeight: 600 }} /> : '-'}
            </TableCell>
          );
        })}
        <TableCell align="right"><strong>{total}</strong></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={Object.keys(MODULE_LABELS).length + 2} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, px: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Page-level usage for {row.schoolName}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Visits</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Usage %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(row.pages || []).map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.pageLabel || p.pagePath}</TableCell>
                      <TableCell><Chip label={MODULE_LABELS[p.moduleName] || p.moduleName} size="small" sx={{ bgcolor: (MODULE_COLORS[p.moduleName] || '#666') + '22', color: MODULE_COLORS[p.moduleName] || '#666' }} /></TableCell>
                      <TableCell align="right">{p.count}</TableCell>
                      <TableCell><PercentBar value={p.percent} color={MODULE_COLORS[p.moduleName]} /></TableCell>
                    </TableRow>
                  ))}
                  {(!row.pages || row.pages.length === 0) && (
                    <TableRow><TableCell colSpan={4} align="center">No page data</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ModuleUsagePage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYearId, setAcademicYearId] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchJson('/schools');
        setSchools(Array.isArray(res?.schools) ? res.schools : Array.isArray(res) ? res : []);
      } catch (_) {}
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (schoolId) params.set('schoolId', schoolId);
      if (academicYearId) params.set('academicYearId', academicYearId);
      const res = await fetchJson(`/api/super-admin/module-usage?${params.toString()}`);
      setData(res);
      // Populate academic years from response
      if (res?.academicYears) setAcademicYears(res.academicYears);
      // Auto-select the current academic year on first load
      if (!academicYearId && res?.selectedAcademicYear?.id) {
        setAcademicYearId(String(res.selectedAcademicYear.id));
      }
    } catch (e) {
      setError(e?.message || 'Failed to load module usage');
    } finally {
      setLoading(false);
    }
  }, [schoolId, academicYearId]);

  useEffect(() => { load(); }, [load]);

  const moduleEntries = data?.byModule ? Object.entries(data.byModule).sort((a, b) => b[1] - a[1]) : [];
  const totalActions = moduleEntries.reduce((s, [, c]) => s + c, 0);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" mb={1}>{t('moduleUsage.title', 'Module Usage Analytics')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('moduleUsage.desc', 'Real-time percentage usage of pages within each module per academic year.')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('moduleUsage.academicYear', 'Academic Year')}</InputLabel>
              <Select value={academicYearId} onChange={e => setAcademicYearId(e.target.value)} label="Academic Year">
                {academicYears.map(ay => (
                  <MenuItem key={ay.id} value={String(ay.id)}>
                    {ay.name}{ay.isCurrent ? ' (Current)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('moduleUsage.school', 'School')}</InputLabel>
              <Select value={schoolId} onChange={e => setSchoolId(e.target.value)} label="School">
                <MenuItem value="">{t('moduleUsage.allSchools', 'All Schools')}</MenuItem>
                {schools.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" fullWidth onClick={load}>{t('moduleUsage.refresh', 'Refresh')}</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={t('moduleUsage.summary', 'Module Summary')} />
            <Tab label={t('moduleUsage.perPage', 'Per-Page Breakdown')} />
            <Tab label={t('moduleUsage.perSchool', 'Per-School Breakdown')} />
          </Tabs>

          {/* Tab 0: Module Summary */}
          {tab === 0 && (
            <Paper variant="outlined" sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t('moduleUsage.module', 'Module')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('moduleUsage.visits', 'Visits')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {moduleEntries.length === 0 ? (
                    <TableRow><TableCell colSpan={3} align="center">{t('moduleUsage.noData', 'No data yet. Usage will appear once users visit tracked pages.')}</TableCell></TableRow>
                  ) : moduleEntries.map(([mod, count]) => {
                    const pct = totalActions > 0 ? parseFloat(((count / totalActions) * 100).toFixed(1)) : 0;
                    return (
                      <TableRow key={mod}>
                        <TableCell><Chip label={MODULE_LABELS[mod] || mod} size="small" sx={{ bgcolor: (MODULE_COLORS[mod] || '#666') + '22', color: MODULE_COLORS[mod] || '#666', fontWeight: 600 }} /></TableCell>
                        <TableCell align="right">{count}</TableCell>
                        <TableCell><PercentBar value={pct} color={MODULE_COLORS[mod]} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Tab 1: Per-Page Breakdown (global) */}
          {tab === 1 && (
            <Paper variant="outlined" sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Page</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Visits</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Usage %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!data?.globalPages || data.globalPages.length === 0) ? (
                    <TableRow><TableCell colSpan={4} align="center">{t('moduleUsage.noData', 'No data yet.')}</TableCell></TableRow>
                  ) : data.globalPages.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.pageLabel || p.pagePath}</TableCell>
                      <TableCell><Chip label={MODULE_LABELS[p.moduleName] || p.moduleName} size="small" sx={{ bgcolor: (MODULE_COLORS[p.moduleName] || '#666') + '22', color: MODULE_COLORS[p.moduleName] || '#666' }} /></TableCell>
                      <TableCell align="right">{p.count}</TableCell>
                      <TableCell><PercentBar value={p.percent} color={MODULE_COLORS[p.moduleName]} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Tab 2: Per-School Breakdown with expandable page details */}
          {tab === 2 && (
            <Paper variant="outlined" sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t('moduleUsage.school', 'School')}</TableCell>
                    {Object.keys(MODULE_LABELS).map(mod => (
                      <TableCell key={mod} align="center" sx={{ fontWeight: 700, fontSize: 11 }}>{MODULE_LABELS[mod]}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('moduleUsage.total', 'Total')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!data?.schoolBreakdown || data.schoolBreakdown.length === 0) ? (
                    <TableRow><TableCell colSpan={Object.keys(MODULE_LABELS).length + 2} align="center">{t('moduleUsage.noData', 'No data yet.')}</TableCell></TableRow>
                  ) : data.schoolBreakdown.map(row => (
                    <SchoolRow key={row.schoolId} row={row} />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
